import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import styled, { css } from "styled-components";
import OkoAniMouth from "assets/OKO_AniMouth_HigherRez.gif";
import OKOCharacter from "assets/OKO_op2_yellow.svg";
import OKOCharacterNoMouth from "assets/OKO_op2_yellow2.svg";
import OKOCharacterShadow from "assets/Shadow.svg";
import { useDataTrack } from "hooks/livekit/useDataTrack/useDataTrack";
import { colors, constants, renderTextWithCapsColoring, stripEmoji } from "utils";
import { VolumeControl } from "components/shared/VolumeControl/VolumeControl";

export const ConsensusTransitionOkoAssistant = ({
  eventBackgroundColor = colors.blue500,
  surveyData = null,
  onSurveyResponse = null,
  dismissChallengeSurvey = null,
  showChallengeSurvey = false
}) => {
  const [phrase, setPhrase] = useState("");
  const [isPlayingAudio, setIsPlayingAudio] = useState(false);
  const [showText, setShowText] = useState(false);
  const [selectedSurveyOption, setSelectedSurveyOption] = useState(null);
  const [surveyCompleted, setSurveyCompleted] = useState(false);
  const [showVolumeControl, setShowVolumeControl] = useState(false);
  const lastSpeechEventRef = { current: { type: null, time: null } };
  const surveyDialogRef = useRef(null);
  const prepButtonRef = useRef(null);

  useEffect(() => {
    if (surveyData) {
      setSelectedSurveyOption(null);
      setSurveyCompleted(false);
    }
  }, [surveyData]);

  useEffect(() => {
    if (surveyData?.type === "challenge_survey" && showChallengeSurvey && !surveyCompleted) {
      surveyDialogRef.current?.focus();
    }
  }, [surveyData, showChallengeSurvey, surveyCompleted]);

  useEffect(() => {
    if (surveyCompleted) {
      prepButtonRef.current?.focus();
    }
  }, [surveyCompleted]);

  useDataTrack((e) => {
    if (e.event_type === "speech_start") {
      const now = Date.now();
      const gap = lastSpeechEventRef.current.time ? now - lastSpeechEventRef.current.time : null;
      console.log("[oko-patter] speech_start", { message: e?.data?.message?.substring(0, 80), gapFromLast: gap ? `${gap}ms (from ${lastSpeechEventRef.current.type})` : "first" });
      lastSpeechEventRef.current = { type: "speech_start", time: now };
      setIsPlayingAudio(true);
      setShowText(true);
      setPhrase(stripTags(e.data.message));
    }
    if (e.event_type === "speech_end") {
      const now = Date.now();
      const gap = lastSpeechEventRef.current.time ? now - lastSpeechEventRef.current.time : null;
      console.log("[oko-patter] speech_end", { duration: gap ? `${gap}ms (from ${lastSpeechEventRef.current.type})` : "unknown" });
      lastSpeechEventRef.current = { type: "speech_end", time: now };
      setTimeout(() => {
        setIsPlayingAudio(false);
        if (!surveyCompleted) {
          setShowText(false);
        }
      }, 750);
    }
    if (e.type === "student_text_message" && e.text) {
      setPhrase(e.text);
      setShowText(true);
    }
  });

  const handleSurveyOptionClick = (optionIndex, option) => {
    if (surveyCompleted) return;

    setSelectedSurveyOption(optionIndex);
    setSurveyCompleted(true);
    setPhrase("");

    if (onSurveyResponse) {
      const surveyType = surveyData?.type || 'survey';
      onSurveyResponse(surveyType, option.value);
    }
  };

  const handlePrepForTeamTime = () => {
    if (dismissChallengeSurvey) {
      dismissChallengeSurvey();
    }
  };

  const okoCharacter = isPlayingAudio ? OKOCharacterNoMouth : OKOCharacter;
  // `compact` shrinks the bot at tablet width — only wanted on the session
  // survey, where the bubble shares the screen with it; the Solo/Team Time
  // phrase overlays keep the full-size bot.
  const renderAssistantIcon = (compact = false) =>
    <IconWrapper
      onMouseEnter={() => setShowVolumeControl(true)}
      onMouseLeave={() => setShowVolumeControl(false)}
    >
      <Icon style={{ display: "grid" }} $compact={compact}>
        <img src={okoCharacter} style={{ width: "100%", zIndex: 10 }} alt="character" />
        <img src={OKOCharacterShadow} style={{ position: "relative", top: "-17px", width: "100%" }} alt="shadow" />
        <IconMouth $compact={compact} style={{ backgroundImage: `url(${isPlayingAudio ? OkoAniMouth : "none"})` }} />
      </Icon>
      {showVolumeControl && <VolumeControl />}
    </IconWrapper>;

  const shouldShowChallengeSurvey = surveyData?.type === "challenge_survey";
  const shouldShowSessionSurvey = surveyData && !shouldShowChallengeSurvey;
  const shouldShowPhrase = phrase && showText && !surveyData;

  return (
    <>
      <div style={{ display: "flex", flexDirection: "column", minHeight: "275px", width: "317px", gap: "24px" }}>
        <CharacterContainer>
          {!shouldShowChallengeSurvey && renderAssistantIcon(shouldShowSessionSurvey)}
          {shouldShowPhrase && (
            <SpeechBubble>
              <div style={{ fontSize: "22px", fontWeight: 500, lineHeight: "normal" }}>
                {renderTextWithCapsColoring(trimPhrase(phrase), eventBackgroundColor)}
              </div>
            </SpeechBubble>
          )}
        </CharacterContainer>
      </div>

      {/* Challenge Survey - New full-screen centered design */}
      {shouldShowChallengeSurvey && showChallengeSurvey && (
        <ChallengeSurveyOverlay>
          <AnimatePresence>
            {!surveyCompleted ? (
              <ChallengeSurveyContent
                as={motion.div}
                ref={surveyDialogRef}
                role="dialog"
                aria-modal="true"
                aria-labelledby="challenge-survey-question"
                aria-describedby="challenge-survey-options-spoken"
                tabIndex={-1}
                transition={{
                  type: "spring",
                  damping: 25,
                  stiffness: 300,
                }}
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
              >
                <SurveyOkoBot>
                  {renderAssistantIcon()}
                  <SurveyBubbleAbsolute>
                    <ChatBubble>
                      <motion.div
                        transition={{ delay: 0.2 }}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        style={{ display: "flex", flexDirection: "column", gap: "24px", width: "100%" }}
                      >
                        <SurveyQuestion id="challenge-survey-question" role="heading" aria-level="2">
                          {surveyData?.question}
                        </SurveyQuestion>
                        <SurveyOptions role="group" aria-labelledby="challenge-survey-question">
                          {surveyData?.options?.map((option, index) => (
                            <SurveyOption
                              key={index}
                              onClick={() => handleSurveyOptionClick(index, option)}
                              selected={selectedSurveyOption === index}
                              disabled={surveyCompleted}
                              aria-label={stripEmoji(option.label)}
                            >
                              {option.label}
                            </SurveyOption>
                          ))}
                        </SurveyOptions>
                        <SrOnly id="challenge-survey-options-spoken">
                          {`${surveyData?.options?.length || 0} choices: ` +
                            (surveyData?.options || [])
                              .map((option, index) => `${index + 1}. ${stripEmoji(option.label)}`)
                              .join(". ")}
                        </SrOnly>
                      </motion.div>
                    </ChatBubble>
                  </SurveyBubbleAbsolute>
                </SurveyOkoBot>
              </ChallengeSurveyContent>
            ) : (
              <CompletedSurveyCard
                as={motion.div}
                transition={{
                  type: "spring",
                  damping: 25,
                  stiffness: 300,
                }}
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
              >
                <CompletedBotAndBubble>
                  {renderAssistantIcon()}
                  {phrase && (
                    <CompletedBubble>
                      <FeedbackBody>
                        {renderTextWithCapsColoring(trimPhrase(phrase), eventBackgroundColor)}
                      </FeedbackBody>
                    </CompletedBubble>
                  )}
                </CompletedBotAndBubble>
                <CompletedCtaSection
                  as={motion.div}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 }}
                >
                  <CtaPromptText>
                    Get ready while you wait!{"\n"}
                    Write down a question to ask your team! ✍️
                  </CtaPromptText>
                  <PrepButton
                    ref={prepButtonRef}
                    type="button"
                    onClick={handlePrepForTeamTime}
                    aria-label="Prep for team time"
                  >
                    Prep for Team Time
                  </PrepButton>
                </CompletedCtaSection>
              </CompletedSurveyCard>
            )}
          </AnimatePresence>
        </ChallengeSurveyOverlay>
      )}

      {/* Session Survey - existing bubble design */}
      {shouldShowSessionSurvey && (
        <SurveyOverlay>
          <AnimatePresence>
            <SurveyBubble
              surveyCompleted={surveyCompleted}
              surveyType={surveyData?.type}
              as={motion.div}
              transition={{
                type: "spring",
                damping: 25,
                stiffness: 300,
              }}
              initial={{ y: "100vh" }}
              animate={{ y: 0 }}
              exit={{ y: "100vh" }}
            >
              {!surveyCompleted ? (
                <SessionSurveyContainer
                  transition={{
                    delay: 0.2,
                  }}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                >
                  <SessionSurveyQuestion>
                    {surveyData?.question}
                  </SessionSurveyQuestion>
                  <SurveyOptions>
                    {surveyData?.options?.map((option, index) => (
                      <SurveyOption
                        key={index}
                        onClick={() => handleSurveyOptionClick(index, option)}
                        selected={selectedSurveyOption === index}
                        disabled={surveyCompleted}
                      >
                        {option.label}
                      </SurveyOption>
                    ))}
                  </SurveyOptions>
                </SessionSurveyContainer>) :
                <p style={{
                  fontSize: "22px",
                  fontWeight: 500,
                  lineHeight: "140%",
                  color: colors.blue950
                }}>{surveyData?.options[selectedSurveyOption]?.response}</p>
              }
            </SurveyBubble>
          </AnimatePresence>
        </SurveyOverlay>
      )}
    </>
  );
};

function stripTags(str) {
  if (str === null || str === "") return false;
  else str = str.toString();
  return str.replace(/<[^>]*>/g, "");
}

// The following function is needed as "text-overflow: ellipsis" works only with "white-space: no-wrap" in CSS.
// Adding the latter results in Oko Assistant phrase being limited to 1 line.
function trimPhrase(str) {
  const maxLength = 230;
  return str.length > maxLength ? str.substring(0, maxLength - 3) + "..." : str.substring(0, maxLength);
}

const CharacterContainer = styled.div`
  position: relative;
  display: flex;
  left: calc(50% - 250px / 2);
  top: calc(40% - 250px / 2);
`;

const IconWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  padding-left: 60px;
  margin-left: -60px;
`;

const Icon = styled.div`
  align-items: center;
  display: flex;
  justify-content: center;
  width: 250px;
  height: 250px;

  ${({ $compact }) => $compact && css`
    @media ${constants.TABLET_MEDIA_QUERY} {
      width: 180px;
      height: 180px;
    }
  `}
`;

const IconMouth = styled.div`
  width: 126px;
  height: 126px;
  left: 129px;
  top: 65px;
  border-radius: 100px;
  position: absolute;
  background-size: cover;
  z-index: 10;

  ${({ $compact }) => $compact && css`
    @media ${constants.TABLET_MEDIA_QUERY} {
      width: 91px;
      height: 91px;
      left: 93px;
      top: 47px;
    }
  `}
`;

const SpeechBubble = styled.div`
  position: absolute;
  display: flex;
  justify-content: center;
  align-items: flex-end;
  padding: 24px;
  max-width: 339px;
  min-width: 339px;
  border-radius: 32px 32px 32px 0;
  background: ${colors.blue100};
  left: 245px;
  bottom: 112px;
  z-index: 10;

  &::before {
    content: '';
    position: absolute;
    bottom: -20px;
    left: -18px;
    width: 0;
    height: 0;
    border-top: 20px solid ${colors.blue100};
    border-right: 20px solid ${colors.blue100};
    border-bottom: 20px solid transparent;
    border-left: 20px solid transparent;
    transform: rotate(-45deg);
  }
`;

/* ===== Challenge Survey (new design) ===== */

const SrOnly = styled.span`
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
`;

const ChallengeSurveyOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  z-index: 200;
  pointer-events: auto;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ChallengeSurveyContent = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
`;

const SurveyOkoBot = styled.div`
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%) scale(0.8);
  z-index: 1;
`;

const SurveyBubbleAbsolute = styled.div`
  position: absolute;
  left: 230px;
  bottom: 110px;
  width: 385px;
  z-index: 10;
`;

const ChatBubble = styled.div`
  background: ${colors.blue100};
  border-radius: 20px 20px 20px 0;
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 24px;
  align-items: center;
  justify-content: center;
  position: relative;

  &::before {
    content: '';
    position: absolute;
    bottom: -20px;
    left: -18px;
    width: 0;
    height: 0;
    border-top: 20px solid ${colors.blue100};
    border-right: 20px solid ${colors.blue100};
    border-bottom: 20px solid transparent;
    border-left: 20px solid transparent;
    transform: rotate(-45deg);
  }
`;

const SurveyQuestion = styled.div`
  font-size: 22px;
  font-weight: 600;
  color: ${colors.blue950};
  line-height: 1.4;
  width: 100%;
`;

const SurveyOptions = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
`;

const SurveyOption = styled.button.attrs({ type: "button" })`
  padding: 12px 16px;
  border-radius: 100px;
  background: ${props => props.selected ? colors.blue500 : 'white'};
  color: ${props => props.selected ? 'white' : colors.blue500};
  border: 1px solid ${colors.blue500};
  cursor: ${props => props.disabled ? 'default' : 'pointer'};
  height: 48px;
  font-size: 18px;
  font-weight: 600;
  display: flex;
  align-items: center;
  text-align: left;
  font-family: inherit;
  transition: all 0.2s ease;
  opacity: ${props => props.disabled && !props.selected ? 0.6 : 1};

  &:hover {
    ${props => !props.disabled && !props.selected && `
      background: ${colors.blue50};
      border-color: ${colors.blue500};
    `}
  }

  &:focus-visible {
    outline: 2px solid ${colors.blue800};
    outline-offset: 2px;
  }

  /* Narrower tablet bubbles wrap longer option labels; grow instead of clipping. */
  @media ${constants.TABLET_MEDIA_QUERY} {
    height: auto;
    min-height: 48px;
    font-size: 16px;
  }
`;

const FeedbackContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
`;

const FeedbackTitle = styled.div`
  font-size: 22px;
  font-weight: 600;
  color: ${colors.blue950};
  line-height: 1.4;
`;

const FeedbackBody = styled.div`
  font-size: 22px;
  font-weight: 400;
  color: ${colors.blue950};
  line-height: 1.4;
`;

const CompletedSurveyCard = styled.div`
  background: #faf7f2;
  border-radius: 20px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 25px;
  padding: 40px;
  padding-top: 80px;
  width: 90%;
  max-width: 800px;
  min-height: 500px;
  margin: 40px;
  z-index: 201;
`;

const CompletedBotAndBubble = styled.div`
  display: inline-flex;
  align-items: flex-start;
  position: relative;
`;

const CompletedBubble = styled.div`
  background: ${colors.blue100};
  border-radius: 20px 20px 20px 0;
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-width: 385px;
  margin-left: -20px;
  margin-top: -40px;
  position: relative;
  z-index: 10;

  &::before {
    content: '';
    position: absolute;
    bottom: -14px;
    left: -12px;
    width: 0;
    height: 0;
    border-top: 15px solid ${colors.blue100};
    border-right: 15px solid ${colors.blue100};
    border-bottom: 15px solid transparent;
    border-left: 15px solid transparent;
    transform: rotate(-45deg);
  }
`;

const CompletedCtaSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  align-self: center;
  gap: 24px;
  padding: 0 32px;
`;

const CtaPromptText = styled.p`
  font-size: 24px;
  font-weight: 600;
  line-height: 32px;
  color: black;
  text-align: center;
  white-space: pre-line;
  margin: 0;
`;

const PrepButton = styled.button`
  background: ${colors.pink500};
  color: white;
  border: none;
  border-radius: 20px;
  padding: 16px 24px;
  height: 64px;
  font-size: 16px;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  transition: opacity 0.2s ease;

  &:hover {
    opacity: 0.9;
  }
`;

/* ===== Session Survey (existing design) ===== */

const SurveyOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  z-index: 200;
  pointer-events: none;
  display: flex;
  align-items: flex-end;
  justify-content: center;
`;

const SurveyBubble = styled.div`
  position: absolute;
  display: flex;
  justify-content: center;
  align-items: flex-end;
  padding: 24px;
  max-width: ${props => props.surveyCompleted ? "385px" : "464px"};
  min-width: ${props => props.surveyCompleted ? "385px" : "464px"};
  border-radius: 32px 32px 32px 0;
  background: ${colors.blue100};
  left: calc(50% + 120px);
  top: ${props => props.surveyCompleted ? "calc(50% - 299px)" : "calc(50% - 290px)"};
  z-index: 10;
  pointer-events: auto;

  /* Anchored 120px right of center with a fixed width, the bubble's right edge
     lands off screen below ~1200px. Pinning the right edge keeps the full
     design width (the bubble slides left over the bot instead of shrinking,
     which made it grow too tall and clip below). */
  @media (max-width: 1200px) {
    left: auto;
    right: 12px;
    min-width: 0;
    max-width: none;
    width: min(${props => (props.surveyCompleted ? "385px" : "464px")}, calc(50vw - 12px));
  }

  &::before {
    content: '';
    position: absolute;
    bottom: -20px;
    left: -18px;
    width: 0;
    height: 0;
    border-top: 20px solid ${colors.blue100};
    border-right: 20px solid ${colors.blue100};
    border-bottom: 20px solid transparent;
    border-left: 20px solid transparent;
    transform: rotate(-45deg);
  }
`;

const SessionSurveyContainer = styled(motion.div)`
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
`;

const SessionSurveyQuestion = styled.div`
  font-size: 22px;
  font-weight: 600;
  color: ${colors.blue950};
  text-align: center;
  line-height: 1.4;

  @media ${constants.TABLET_MEDIA_QUERY} {
    font-size: 18px;
  }
`;
